import React, { useState, useEffect, useMemo } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter, Form, FormGroup, Label, Input, FormFeedback, Button, Col, Row } from 'reactstrap';
import { toast } from 'react-toastify';
import PropTypes from 'prop-types';
import FilesComponent from '../CallForms/Component/FilesComponent';
import { CHASSIS_VIN_REGEX, validateLicensePlate, validatePhoneTr, validateTcid, normalizeFieldValue, displayDecimal } from '../../../_helper/regexHandler';
import { useModalCrudState } from '../../../_helper/hooks/useModalCrudState';

const toLocalISOString = (date) => {
    if (!date) return '';
    try {
        const dateObj = new Date(date);
        if (isNaN(dateObj.getTime())) return '';
        const tzOffset = dateObj.getTimezoneOffset() * 60000;
        const localDate = new Date(dateObj.getTime() - tzOffset);
        return localDate.toISOString().slice(0, 16);
    } catch {
        return '';
    }
};

const EMPTY_ARRAY = [];

const JobDataModal = ({ apiEndpoint, isOpen, toggle, mode, selectedJob, refreshData }) => {
    const initialJobData = {
        id: '',
        plate: '',
        chassisNumber: '',
        accidentDate: '',
        reportDate: '',
        tcid: '',
        fullName: '',
        phone: [{ phone: '' }, { phone: '' }],
        faultStatus: '',
        reference: '',
        originFolder: '',
    };

    const validationSchema = useMemo(() => ({
        plate: {
            required: true,
            message: 'Plaka zorunludur.',
            validator: validateLicensePlate,
            validatorMessage: 'Geçerli bir plaka giriniz (örn: 34 ABC 123).',
        },
        chassisNumber: {
            required: true,
            message: 'Şase Numarası zorunludur.',
            regex: CHASSIS_VIN_REGEX,
            regexMessage: 'Geçerli bir 17 haneli şase numarası girin (I, O, Q hariç).',
        },
        accidentDate: { required: true, message: 'Kaza Tarihi zorunludur.' },
        reportDate: { required: true, message: 'Rapor Tarihi zorunludur.' },
        tcid: {
            required: true,
            message: 'TC Kimlik Numarası zorunludur.',
            validator: validateTcid,
            validatorMessage: 'TC Kimlik Numarası 11 haneli rakam olmalıdır.',
        },
        fullName: {
            required: true,
            message: 'Ad Soyad zorunludur.',
            minLength: 2,
            minLengthMessage: 'Ad Soyad en az 2 karakter olmalıdır.',
        },
        faultStatus: {
            required: true,
            message: 'Kusur Durumu zorunludur.',
            min: 0,
            max: 100,
            rangeMessage: 'Kusur Durumu 0 ile 100 arasında bir sayı olmalıdır.',
        },
        reference: {
            minLength: 2,
            minLengthMessage: 'Referans adı en az 2 karakter olmalıdır.',
        },
    }), []);

    const {
        formData: jobData,
        setFormData: setJobData,
        errors,
        setErrors,
        handleCancel: handleCrudCancel,
        handleInputChange,
        handleSubmit: hookHandleSubmit,
        isSubmitting,
    } = useModalCrudState({
        isOpen,
        mode,
        selectedItem: selectedJob,
        initialData: initialJobData,
        toggle,
        mapSelectedToData: ({ initialData, selectedItem }) => {
            const phones = Array.isArray(selectedItem?.phone) ? selectedItem.phone : [];
            const formPhones = [
                phones[0] || { phone: '' },
                phones[1] || { phone: '' },
            ];
            const formattedAccidentDate = toLocalISOString(selectedItem?.accidentDate);
            const formattedReportDate = toLocalISOString(selectedItem?.reportDate);

            return {
                ...initialData,
                ...selectedItem,
                accidentDate: formattedAccidentDate,
                reportDate: formattedReportDate,
                phone: formPhones,
                tcid: String(selectedItem?.tcid || ''),
                faultStatus: selectedItem?.faultStatus !== null && selectedItem?.faultStatus !== undefined ? String(selectedItem.faultStatus) : '',
                reference: selectedItem?.reference || '',
                originFolder: selectedItem?.originFolder || '',
            };
        },
        normalizeInput: true,
        validationSchema,
        submitConfig: {
            apiEndpoint: mode === 'add' ? `${apiEndpoint}/manual` : apiEndpoint,
            refreshData,
            componentName: 'İş Kaydı',
            onSuccess: (result) => {
                if (mode === 'add' && result?.id) {
                    setCurrentJobId(result.id);
                    toast.success('İş kaydı oluşturuldu. Artık dosya yükleyebilirsiniz.');
                    return;
                }
                toggle();
            },
            buildPayload: (data) => {
                const accidentDateToSend = data.accidentDate ? new Date(data.accidentDate).toISOString() : null;
                const reportDateToSend = data.reportDate ? new Date(data.reportDate).toISOString() : null;

                const phonesToSend = data.phone
                    .map(p => ({ phone: p.phone?.trim() || '' }))
                    .filter(p => p.phone);

                const payload = {
                    plate: data.plate,
                    chassisNumber: data.chassisNumber,
                    accidentDate: accidentDateToSend,
                    reportDate: reportDateToSend,
                    tcid: data.tcid,
                    fullName: data.fullName,
                    phone: phonesToSend,
                    faultStatus: Number(data.faultStatus),
                    reference: data.reference || null,
                    originFolder: data.originFolder || null,
                };

                if (mode === 'modify' && selectedJob?.id) {
                    payload.id = selectedJob.id;
                }

                return payload;
            },
        },
    });

    const [existingFiles, setExistingFiles] = useState(EMPTY_ARRAY);
    const [currentJobId, setCurrentJobId] = useState(null);

    useEffect(() => {
        if (isOpen) {
            if (mode === 'modify' && selectedJob) {
                setCurrentJobId(selectedJob.id);
                setExistingFiles(selectedJob.files || EMPTY_ARRAY);
            } else {
                setExistingFiles(EMPTY_ARRAY);
                setCurrentJobId(null);
            }
        } else {
            setExistingFiles(EMPTY_ARRAY);
            setCurrentJobId(null);
        }
    }, [mode, selectedJob, isOpen]);

    const handlePhoneChange = (index, value) => {
        let processedValue = normalizeFieldValue('phoneNumber', value);
        if (processedValue && !processedValue.startsWith('05')) {
            if (processedValue.startsWith('5')) {
                processedValue = `0${processedValue}`;
            } else {
                processedValue = '';
            }
        }

        const updatedPhones = [...jobData.phone];
        updatedPhones[index] = { phone: processedValue };

        setJobData(prevData => ({ ...prevData, phone: updatedPhones }));

        const errorKey = `phone_${index}`;
        if (errors[errorKey]) {
            setErrors(prevErrors => ({ ...prevErrors, [errorKey]: '' }));
        }
    };

    const handleCancel = () => {
        setExistingFiles(EMPTY_ARRAY);
        setCurrentJobId(null);
        handleCrudCancel();
    };

    // Telefon validasyonu schema'ya sığmadığı için submit öncesi wrapper
    const handleOperation = async (e) => {
        const phoneErrors = {};
        const phone1 = jobData.phone[0]?.phone?.trim() || '';
        if (!phone1) {
            phoneErrors.phone_0 = 'Telefon 1 zorunludur.';
        } else if (!validatePhoneTr(phone1)) {
            phoneErrors.phone_0 = 'Telefon 1, 05 ile başlamalı ve 11 haneli olmalıdır.';
        }

        const phone2 = jobData.phone[1]?.phone?.trim() || '';
        if (phone2 && !validatePhoneTr(phone2)) {
            phoneErrors.phone_1 = 'Telefon 2, 05 ile başlamalı ve 11 haneli olmalıdır.';
        }

        if (Object.keys(phoneErrors).length > 0) {
            setErrors(prev => ({ ...prev, ...phoneErrors }));
            toast.error('Lütfen formdaki hataları düzeltin.');
            return;
        }

        await hookHandleSubmit(e);
    };

    return (
        <Modal isOpen={isOpen} toggle={handleCancel} size="lg" backdrop="static">
            <ModalHeader toggle={handleCancel}>
                {mode === 'add' ? 'Manuel İş Kaydı Ekle' : 'İş Kaydı Düzenle'}
            </ModalHeader>
            <ModalBody>
                <Form>
                    <Row>
                        <Col md={6}>
                            <FormGroup>
                                <Label for="plate">Plaka <span className="text-danger">*</span></Label>
                                <Input
                                    type="text"
                                    name="plate"
                                    id="plate"
                                    value={jobData.plate || ''}
                                    onChange={handleInputChange}
                                    placeholder="Örn: 34 AB 123"
                                    invalid={!!errors.plate}
                                />
                                <FormFeedback>{errors.plate}</FormFeedback>
                            </FormGroup>
                        </Col>
                        <Col md={6}>
                            <FormGroup>
                                <Label for="chassisNumber">Şase Numarası <span className="text-danger">*</span></Label>
                                <Input
                                    type="text"
                                    name="chassisNumber"
                                    id="chassisNumber"
                                    value={jobData.chassisNumber || ''}
                                    onChange={handleInputChange}
                                    placeholder="17 Haneli Şase Numarası"
                                    maxLength={17}
                                    invalid={!!errors.chassisNumber}
                                />
                                <FormFeedback>{errors.chassisNumber}</FormFeedback>
                            </FormGroup>
                        </Col>
                    </Row>

                    <Row>
                        <Col md={6}>
                            <FormGroup>
                                <Label for="accidentDate">Kaza Tarihi <span className="text-danger">*</span></Label>
                                <Input
                                    type="datetime-local"
                                    name="accidentDate"
                                    id="accidentDate"
                                    value={jobData.accidentDate || ''}
                                    onChange={handleInputChange}
                                    invalid={!!errors.accidentDate}
                                />
                                <FormFeedback>{errors.accidentDate}</FormFeedback>
                            </FormGroup>
                        </Col>
                        <Col md={6}>
                            <FormGroup>
                                <Label for="reportDate">Rapor Tarihi <span className="text-danger">*</span></Label>
                                <Input
                                    type="datetime-local"
                                    name="reportDate"
                                    id="reportDate"
                                    value={jobData.reportDate || ''}
                                    onChange={handleInputChange}
                                    invalid={!!errors.reportDate}
                                />
                                <FormFeedback>{errors.reportDate}</FormFeedback>
                            </FormGroup>
                        </Col>
                    </Row>

                    <Row>
                        <Col md={6}>
                            <FormGroup>
                                <Label for="tcid">TC Kimlik Numarası <span className="text-danger">*</span></Label>
                                <Input
                                    type="text"
                                    name="tcid"
                                    id="tcid"
                                    value={jobData.tcid || ''}
                                    onChange={handleInputChange}
                                    placeholder="11 Haneli TC Kimlik No"
                                    maxLength={11}
                                    invalid={!!errors.tcid}
                                />
                                <FormFeedback>{errors.tcid}</FormFeedback>
                            </FormGroup>
                        </Col>
                        <Col md={6}>
                            <FormGroup>
                                <Label for="fullName">Ad Soyad <span className="text-danger">*</span></Label>
                                <Input
                                    type="text"
                                    name="fullName"
                                    id="fullName"
                                    value={jobData.fullName || ''}
                                    onChange={handleInputChange}
                                    placeholder="Müşteri Adı Soyadı"
                                    invalid={!!errors.fullName}
                                />
                                <FormFeedback>{errors.fullName}</FormFeedback>
                            </FormGroup>
                        </Col>
                    </Row>

                    <FormGroup>
                        <Label for="reference">Kimin Referansı?</Label>
                        <Input
                            type="text"
                            name="reference"
                            id="reference"
                            value={jobData.reference || ''}
                            onChange={handleInputChange}
                            placeholder="Referans Adı Soyadı (Opsiyonel)"
                            invalid={!!errors.reference}
                        />
                        <FormFeedback>{errors.reference}</FormFeedback>
                    </FormGroup>

                    <Row>
                        <Col md={6}>
                            <FormGroup>
                                <Label for="phone_0">Telefon 1 <span className="text-danger">*</span></Label>
                                <Input
                                    type="tel"
                                    id="phone_0"
                                    name="phone_0"
                                    value={jobData.phone[0]?.phone || ''}
                                    onChange={(e) => handlePhoneChange(0, e.target.value)}
                                    placeholder="05XX XXX XX XX"
                                    maxLength={11}
                                    invalid={!!errors.phone_0}
                                />
                                <FormFeedback>{errors.phone_0}</FormFeedback>
                            </FormGroup>
                        </Col>
                        <Col md={6}>
                            <FormGroup>
                                <Label for="phone_1">Telefon 2</Label>
                                <Input
                                    type="tel"
                                    id="phone_1"
                                    name="phone_1"
                                    value={jobData.phone[1]?.phone || ''}
                                    onChange={(e) => handlePhoneChange(1, e.target.value)}
                                    placeholder="05XX XXX XX XX (Opsiyonel)"
                                    maxLength={11}
                                    invalid={!!errors.phone_1}
                                />
                                <FormFeedback>{errors.phone_1}</FormFeedback>
                            </FormGroup>
                        </Col>
                    </Row>

                    <FormGroup>
                        <Label for="faultStatus">Kusur Durumu (%) <span className="text-danger">*</span></Label>
                        <Input
                            type="text"
                            name="faultStatus"
                            id="faultStatus"
                            value={displayDecimal(jobData.faultStatus || '')}
                            onChange={handleInputChange}
                            placeholder="0-100"
                            invalid={!!errors.faultStatus}
                        />
                        <FormFeedback>{errors.faultStatus}</FormFeedback>
                    </FormGroup>

                    <FormGroup>
                        <Label for="originFolder">Kaynak Klasör</Label>
                        <Input
                            type="text"
                            name="originFolder"
                            id="originFolder"
                            value={jobData.originFolder || ''}
                            disabled={true}
                            placeholder="Yüklenen klasör ismi"
                        />
                    </FormGroup>

                    <hr className="mt-4 mb-4"/>
                    <FormGroup>
                        <FilesComponent
                            onFilesChange={() => {}}
                            existingFiles={existingFiles}
                            multiple={true}
                            callFormReference={false}
                            referenceId={currentJobId}
                            typeFilter={null}
                            title="İş Belgeleri"
                            canEdit={true}
                        />
                    </FormGroup>

                </Form>
            </ModalBody>
            <ModalFooter>
                <Button color="outline-success" className="rounded-pill" onClick={handleOperation} disabled={isSubmitting}>
                    {isSubmitting ? 'Kaydediliyor...' : (mode === 'add' ? 'Ekle' : 'Güncelle')}
                </Button>
                <Button color="outline-danger" onClick={handleCancel} className="ms-2 rounded-pill" disabled={isSubmitting}>
                    İptal
                </Button>
            </ModalFooter>
        </Modal>
    );
};

JobDataModal.propTypes = {
    apiEndpoint: PropTypes.string.isRequired,
    isOpen: PropTypes.bool.isRequired,
    toggle: PropTypes.func.isRequired,
    mode: PropTypes.oneOf(['add', 'modify']).isRequired,
    selectedJob: PropTypes.object,
    refreshData: PropTypes.func.isRequired,
};

export default JobDataModal;
